/**
 * SearchaniseAdapter
 *
 * REST-based adapter for Searchanise (Smart Search & Discovery) Shopify app.
 * Calls the REST API directly rather than depending on the JS SDK widget —
 * works on any URL (local dev, theme editor preview, published store)
 * regardless of whether the App Embed is active.
 *
 * Public surface:
 *   window.SearchaniseAdapter.search(query, options)         -> Promise<dropdown shape>
 *   window.SearchaniseAdapter.searchProducts(options)        -> Promise<rich shape>
 *   window.SearchaniseAdapter.isReady()                      -> boolean
 *   window.SearchaniseAdapter.SearchUnavailableError         -> class
 *
 * search() is the header-dropdown convenience: query string in, narrow
 * three-facet response out (activity / surface / stain only).
 *
 * searchProducts() is the PLP/full-search method: rich options in (collection
 * scope, sort, pagination, facet filters), full response out (all facets,
 * pagination metadata, attribute names exposed for filter wiring).
 *
 * API key comes from the theme setting (window.SEARCHANISE_API_KEY, emitted by
 * theme.liquid) so the same theme works across stores. No hardcoded fallback:
 * a blank key disables search rather than querying another store's catalog.
 */
(function () {
  'use strict';

  const REST_BASE = 'https://searchserverapi1.com/getresults';
  /* Per-store key from the theme setting (window.SEARCHANISE_API_KEY). No
     hardcoded fallback: a blank key leaves isReady() false and search disabled,
     rather than querying another store. Set it per store in Theme settings. */
  const API_KEY = (typeof window !== 'undefined' && window.SEARCHANISE_API_KEY
    ? String(window.SEARCHANISE_API_KEY).trim()
    : '');

  /* Config failures arrive as plain-text bodies. Two of the three are HTTP 200,
     so res.ok does not catch them. Retrying never resolves any of them. */
  const SENTINELS = ['EMPTY_API_KEY', 'ENGINE_REMOVED', 'SEARCH_DATA_NOT_IMPORTED'];

  /* Header dropdown defaults. The dropdown shows 3-4 products max and
     caps facets per category for scannability. */
  const DROPDOWN_PRODUCT_LIMIT = 4;
  const DROPDOWN_FACETS_PER_CATEGORY = 5;

  /* PLP / search results defaults. The full grid shows 24/page with no
     facet cap (the side drawer expands "show more" past the threshold). */
  const PLP_PRODUCT_LIMIT = 24;

  /* Metafield title -> dropdown facet key mapping. Only used by the
     dropdown shape adapter; the PLP rail iterates the full facets array
     directly without name-based lookups. */
  const DROPDOWN_FACET_TITLES = {
    activity: 'custom.activity',
    surface:  'custom.surface_type',
    stain:    'custom.stain_type'
  };

  /* Facets the PLP rail should NOT render. Activity is surfaced as the
     tile section above the grid; Tags are SKU-only on Star brite's
     catalog. The dropdown shape adapter handles its own narrow list. */
  const PLP_FACETS_HIDDEN = new Set([
    'custom.activity',
    'Tags'
  ]);

  class SearchUnavailableError extends Error {
    constructor(message) {
      super(message || 'Search is temporarily unavailable.');
      this.name = 'SearchUnavailableError';
    }
  }

  function isReady() {
    return Boolean(API_KEY);
  }

  /* =====================================================================
     Public: searchProducts
     The PLP/search-page entry point. Takes a rich options object
     describing the page context + user filter state, returns the full
     normalized Searchanise response.
     =====================================================================

     Options:
       query           string   search query; '*' for collection browse
       collection      string   Shopify collection title to scope to
                                (matches the "collections" facet value)
       filters         object   { attributeName: value | [values] }
                                multi-value array becomes pipe-separated (OR)
       sortBy          string   relevance | title | created | price | discount
       sortOrder       string   asc | desc
       startIndex      number   pagination offset (0-based)
       limit           number   max results to return (default 24)
       includeSuggestions  boolean   ask for suggestions in the response
       signal          AbortSignal   for cancelling in-flight requests
     ===================================================================== */
  async function searchProducts(options) {
    options = options || {};
    if (!API_KEY) {
      const err = new SearchUnavailableError(
        'Searchanise API key is blank. Set it in Theme settings -> Search (Searchanise).'
      );
      err.code = 'EMPTY_API_KEY';
      throw err;
    }
    const url = buildSearchUrl(options);

    let res;
    try {
      res = await fetch(url.toString(), { signal: options.signal });
    } catch (err) {
      if (err.name === 'AbortError') throw err;
      throw new SearchUnavailableError('Network error: ' + err.message);
    }
    const body = await res.text();

    /* Sentinel match precedes the status check: EMPTY_API_KEY arrives at 400,
       the other two at 200, and all three must carry .code either way. */
    const sentinel = SENTINELS.find(function (s) { return body.trim() === s; });
    if (sentinel) {
      const err = new SearchUnavailableError('Searchanise config error: ' + sentinel);
      err.code = sentinel;
      throw err;
    }

    if (!res.ok) {
      throw new SearchUnavailableError(
        'Searchanise REST returned ' + res.status + ' ' + res.statusText + ': ' + body.slice(0, 80)
      );
    }

    let data;
    try {
      data = JSON.parse(body);
    } catch {
      throw new SearchUnavailableError('Searchanise returned non-JSON: ' + body.slice(0, 80));
    }
    return normalize(data, options);
  }

  /* =====================================================================
     Public: search (header-dropdown convenience)
     Wraps searchProducts() and reshapes the response to the narrow
     three-facet structure the dropdown expects. Kept stable so the
     header search controller doesn't need to know about the richer
     PLP response shape.
     ===================================================================== */
  async function search(query, options) {
    options = options || {};
    const rich = await searchProducts({
      query: query,
      limit: options.limit || DROPDOWN_PRODUCT_LIMIT,
      includeSuggestions: true,
      signal: options.signal
    });
    return {
      query: rich.query,
      total: rich.total,
      suggestions: rich.suggestions,
      facets: shapeForDropdown(rich.facets, rich.query),
      /* Dropdown thumbs are ~5rem wide; downsize to _medium to save bandwidth. */
      products: rich.products.map(function (p) {
        return Object.assign({}, p, {
          imageUrl: (p.imageUrl || '').replace('_large.', '_medium.')
        });
      })
    };
  }

  /* =====================================================================
     URL building
     ===================================================================== */
  function buildSearchUrl(options) {
    const url = new URL(REST_BASE);
    url.searchParams.set('api_key', API_KEY);
    /* Searchanise requires q, but on this instance the EMPTY string is the
       match-all sentinel: q=* is treated literally and returns 0 items, while
       q= returns the full catalog. Browse (bare /search) and collection pages
       pass no query, so they must send q='' to match all. */
    url.searchParams.set('q', options.query || '');
    url.searchParams.set('items', 'true');
    url.searchParams.set('facets', 'true');
    if (options.includeSuggestions) {
      url.searchParams.set('suggestions', 'true');
    }
    url.searchParams.set('maxResults', String(options.limit || PLP_PRODUCT_LIMIT));
    if (options.startIndex) {
      url.searchParams.set('startIndex', String(options.startIndex));
    }
    if (options.sortBy) {
      url.searchParams.set('sortBy', options.sortBy);
      if (options.sortOrder) {
        url.searchParams.set('sortOrder', options.sortOrder);
      }
    }

    /* Collection scope. NOT restrictBy[collections]: collection membership is
       not indexed on any store, so that returns 0 items every time. Instead the
       caller passes the metafield facet the collection's own rule is built on
       (e.g. custom.market = "Boating") already translated to its Searchanise
       attribute, and we restrict on that. Omitted entirely for /collections/all,
       which legitimately means "no scope". */
    if (options.scopeAttribute && options.scopeValue) {
      url.searchParams.set('restrictBy[' + options.scopeAttribute + ']', options.scopeValue);
    }

    /* User-applied filters from the side drawer / banner. Keys are
       Searchanise facet attribute names (often filter_metafield_<hash>
       for metafield facets, or plain names like "vendor"). Pipe-joined
       arrays are OR; comma-joined would be AND, but we only emit OR for
       the side drawer's checkbox semantics. */
    if (options.filters) {
      const keys = Object.keys(options.filters);
      for (let i = 0; i < keys.length; i++) {
        const attr = keys[i];
        const value = options.filters[attr];
        if (value === null || value === undefined || value === '') continue;
        const joined = Array.isArray(value) ? value.join('|') : value;
        url.searchParams.set('restrictBy[' + attr + ']', joined);
      }
    }

    return url;
  }

  /* =====================================================================
     Normalize: REST response -> ISearchResponseProps
     ===================================================================== */
  function normalize(rest, options) {
    return {
      query: options.query || '',
      total: rest.totalItems || 0,
      startIndex: rest.startIndex || 0,
      itemsPerPage: rest.itemsPerPage || 0,
      currentItemCount: rest.currentItemCount || 0,
      suggestions: rest.suggestions || [],
      facets: (rest.facets || []).map(normalizeFacet),
      products: (rest.items || []).map(normalizeProduct)
    };
  }

  function normalizeFacet(f) {
    return {
      title: f.title,
      label: friendlyFacetLabel(f.title),
      attribute: f.attribute,
      type: f.type,
      hiddenInRail: PLP_FACETS_HIDDEN.has(f.title) || PLP_FACETS_HIDDEN.has(f.attribute),
      buckets: (f.buckets || []).map(function (b) {
        return {
          value: b.value,
          label: b.value,
          count: typeof b.count === 'string' ? parseInt(b.count, 10) : b.count,
          from: b.from,
          to: b.to
        };
      })
    };
  }

  function normalizeProduct(item) {
    return {
      id: item.product_id,
      title: item.title,
      url: item.link,
      /* Keep the raw image_link (Shopify CDN URL with _large suffix).
         The header dropdown swaps to _medium in shapeForDropdown for its
         tiny cards; the PLP keeps _large since its cards are full-size. */
      imageUrl: item.image_link || '',
      description: truncate(item.description || '', 120),
      price: formatMoney(item.price),
      sku: extractSku(item),
      vendor: item.vendor || null
    };
  }

  /* Strip metafield-namespace prefix and snake_case to give the rail a
     readable label. "custom.surface_type" -> "Surface Type". Falls back
     to the raw title for anything that doesn't fit the pattern. */
  function friendlyFacetLabel(title) {
    if (!title) return '';
    const stripped = title.indexOf('custom.') === 0 ? title.slice(7) : title;
    return stripped
      .split('_')
      .map(function (w) {
        return w.charAt(0).toUpperCase() + w.slice(1);
      })
      .join(' ');
  }

  /* =====================================================================
     Dropdown shape adapter
     Maps the rich facets array to the narrow three-facet structure the
     header dropdown expects.
     ===================================================================== */
  function shapeForDropdown(facets, query) {
    return {
      activity: dropdownFacetBuckets(facets, DROPDOWN_FACET_TITLES.activity, query),
      surface:  dropdownFacetBuckets(facets, DROPDOWN_FACET_TITLES.surface, query),
      stain:    dropdownFacetBuckets(facets, DROPDOWN_FACET_TITLES.stain, query)
    };
  }

  function dropdownFacetBuckets(facets, title, query) {
    const found = facets.find(function (f) { return f.title === title; });
    if (!found || !found.buckets) return [];
    return found.buckets
      .slice()
      .sort(function (a, b) { return b.count - a.count; })
      .slice(0, DROPDOWN_FACETS_PER_CATEGORY)
      .map(function (b) {
        return {
          value: b.value,
          url: buildDropdownFacetUrl(title, b.value, query),
          count: b.count
        };
      });
  }

  /* Dropdown facet links route through Shopify's standard URL filter
     convention, so clicking a facet in the dropdown lands on /search with
     that filter applied. The PLP rail uses the same convention internally
     (JS reads the URL on load, updates it on click). */
  function buildDropdownFacetUrl(title, value, query) {
    return '/search?q=' + encodeURIComponent(query) +
           '&type=product&filter.p.m.' + title + '=' + encodeURIComponent(value);
  }

  /* =====================================================================
     Small utilities
     ===================================================================== */
  function extractSku(item) {
    if (item.product_code) return item.product_code;
    const tags = item.tags || '';
    if (!tags) return null;
    const first = tags.split(',')[0].trim();
    return first.split('[:ATTR:]')[0] || null;
  }

  function truncate(text, max) {
    if (text.length <= max) return text;
    return text.slice(0, max).replace(/\s+\S*$/, '') + '...';
  }

  function formatMoney(price) {
    if (price === null || price === undefined || price === '') return null;
    const n = typeof price === 'string' ? parseFloat(price) : Number(price);
    if (isNaN(n) || n <= 0) return null;
    return '$' + n.toFixed(2);
  }

  window.SearchaniseAdapter = {
    search: search,
    searchProducts: searchProducts,
    isReady: isReady,
    SearchUnavailableError: SearchUnavailableError
  };
})();
